import getPageTypeLabel from '@dashboard/services/getPageTypeLabel';

const phrases = (running, done, failed) => ({
  running,
  done,
  failed,
  stopped: `Stopped while ${running.charAt(0).toLowerCase()}${running.slice(1)}`,
});

const pagePhrases = (verbing, verbed, verb) =>
  Object.fromEntries(
    ['checkout', 'event', 'form'].map(type => {
      const label = getPageTypeLabel({ type });
      return [
        type,
        phrases(
          `${verbing} the ${label}`,
          `${verbed} the ${label}`,
          `Couldn't ${verb} the ${label}`,
        ),
      ];
    }),
  );

const READ_PAGE = pagePhrases('Reading', 'Read', 'read');
const UPDATE_PAGE = pagePhrases('Updating', 'Updated', 'update');
const OTHER = phrases('Using a tool', 'Used a tool', "Couldn't use a tool");

// A refused write (Ask mode) ends as failed, so failed wording must never read as a change.
const STEP_LABELS = {
  get_product: phrases('Reading the product', 'Read the product', "Couldn't read the product"),
  update_product: phrases(
    'Updating the product',
    'Updated the product',
    "Couldn't update the product",
  ),
  create_field: phrases('Adding a field', 'Added a field', "Couldn't add a field"),
  update_field: phrases('Updating a field', 'Updated a field', "Couldn't update a field"),
  delete_field: phrases('Removing a field', 'Removed a field', "Couldn't remove a field"),
  add_stock_photo: phrases('Adding a photo', 'Added a photo', "Couldn't add a photo"),
  edit_text: phrases('Editing the text', 'Edited the text', "Couldn't edit the text"),
  read_link: phrases('Reading your link', 'Read your link', "Couldn't read your link"),
  view_page: phrases('Looking at the page', 'Looked at the page', "Couldn't look at the page"),
  list_coupons: phrases(
    'Checking your discount codes',
    'Checked your discount codes',
    "Couldn't check your discount codes",
  ),
  create_coupon: phrases(
    'Creating a discount code',
    'Created a discount code',
    "Couldn't create a discount code",
  ),
  update_coupon: phrases(
    'Updating a discount code',
    'Updated a discount code',
    "Couldn't update a discount code",
  ),
  list_tax_rates: phrases(
    'Checking your tax rates',
    'Checked your tax rates',
    "Couldn't check your tax rates",
  ),
  create_tax_rate: phrases(
    'Creating a tax rate',
    'Created a tax rate',
    "Couldn't create a tax rate",
  ),
  create_ticket_type: phrases(
    'Adding a ticket type',
    'Added a ticket type',
    "Couldn't add a ticket type",
  ),
  update_ticket_type: phrases(
    'Updating a ticket type',
    'Updated a ticket type',
    "Couldn't update a ticket type",
  ),
  archive_ticket_type: phrases(
    'Archiving a ticket type',
    'Archived a ticket type',
    "Couldn't archive a ticket type",
  ),
  list_theme_properties: phrases(
    'Checking the theme options',
    'Checked the theme options',
    "Couldn't check the theme options",
  ),
  list_themes: phrases('Checking your themes', 'Checked your themes', "Couldn't check your themes"),
  get_theme: phrases('Reading the theme', 'Read the theme', "Couldn't read the theme"),
  create_theme: phrases('Creating a theme', 'Created a theme', "Couldn't create a theme"),
  update_theme: phrases('Updating the theme', 'Updated the theme', "Couldn't update the theme"),
  add_to_website: phrases(
    'Getting the code for your website',
    'Got the code for your website',
    "Couldn't get the code for your website",
  ),
  upload_file: phrases('Uploading a file', 'Uploaded a file', "Couldn't upload a file"),
};

// An event calls its order bumps add-ons; "your" ones are the account's, "the" ones this page's.
const offerLabels = noun => ({
  list_order_bumps: phrases(
    `Checking the ${noun}s`,
    `Checked the ${noun}s`,
    `Couldn't check the ${noun}s`,
  ),
  list_products: phrases(
    `Checking your ${noun}s`,
    `Checked your ${noun}s`,
    `Couldn't check your ${noun}s`,
  ),
  create_product: phrases(
    `Creating an ${noun}`,
    `Created an ${noun}`,
    `Couldn't create an ${noun}`,
  ),
  create_order_bump: phrases(`Adding an ${noun}`, `Added an ${noun}`, `Couldn't add an ${noun}`),
  update_order_bump: phrases(
    `Updating an ${noun}`,
    `Updated an ${noun}`,
    `Couldn't update an ${noun}`,
  ),
  reorder_order_bumps: phrases(
    `Reordering the ${noun}s`,
    `Reordered the ${noun}s`,
    `Couldn't reorder the ${noun}s`,
  ),
  delete_order_bump: phrases(
    `Removing an ${noun}`,
    `Removed an ${noun}`,
    `Couldn't remove an ${noun}`,
  ),
});

const OFFER_LABELS = { checkout: offerLabels('order bump'), event: offerLabels('add-on') };

const PAGE_UPDATE_TOOLS = new Set(['update_checkout_page', 'update_event', 'update_form']);

function labelsOf(name, pageType) {
  if (typeof name !== 'string') return OTHER;
  if (Object.hasOwn(STEP_LABELS, name)) return STEP_LABELS[name];
  const type = Object.hasOwn(UPDATE_PAGE, pageType) ? pageType : 'checkout';
  if (PAGE_UPDATE_TOOLS.has(name)) return UPDATE_PAGE[type];
  const offers = OFFER_LABELS[pageType] ?? OFFER_LABELS.checkout;
  if (Object.hasOwn(offers, name)) return offers[name];
  return name.startsWith('get_') ? READ_PAGE[type] : OTHER;
}

export function toolStepLabel(name, status = 'done', pageType) {
  const labels = labelsOf(name, pageType);
  return labels[status] ?? labels.done;
}

// The model writes what the call did, so only a call that succeeded shows it.
export function stepLabel({ name, status, text }, pageType) {
  if (text && status === 'done') return text;
  return toolStepLabel(name, status, pageType);
}

const laterCalls = (steps, index) =>
  steps.slice(index + 1).filter(later => later.name === steps[index].name);

const WRITE_TOOL =
  /^(create|update|delete|archive|reorder)_|^(add_stock_photo|edit_text|upload_file)$/;

const isWriteTool = name => typeof name === 'string' && WRITE_TOOL.test(name);

// Their cards and the off-topic reply already show these calls.
const HIDDEN_TOOLS = new Set(['point_out', 'offer_upload', 'decline_off_topic']);

// A failed call reads as broken even when the model works around it, so it stays loading while the
// turn runs, until the model calls that tool again. A finished turn drops it, except a change that
// never went through: the seller needs to know it is missing.
export function visibleSteps(steps, { running = false, keepFailedWrites = false } = {}) {
  return steps.flatMap((step, index) => {
    if (HIDDEN_TOOLS.has(step.name)) return [];
    if (step.status !== 'failed') return [step];
    const later = laterCalls(steps, index);
    if (running) return later.length === 0 ? [{ ...step, status: 'running' }] : [];
    // A later failure of the same tool stands for this one too.
    const settled = later.some(call => call.status === 'done' || call.status === 'failed');
    return keepFailedWrites && isWriteTool(step.name) && !settled ? [step] : [];
  });
}
